import type { AnalyticsDeviceType, AnalyticsEvent, AnalyticsEventName } from "@quiz/quiz-core";
import { enqueueAnalyticsEvent } from "./firstParty";

export type AnalyticsPayload = Record<string, string | number | boolean | undefined>;

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

export function emitQuizEvent(event: AnalyticsEventName, payload: AnalyticsPayload = {}): void {
  const analyticsEventId = safeUuid();
  ensureDataLayer().push({ event, analytics_event_id: analyticsEventId, ...payload });
  const firstPartyEvent = toFirstPartyEvent(event, payload, analyticsEventId);
  if (firstPartyEvent) enqueueAnalyticsEvent(firstPartyEvent);
}

export function ensureDataLayer(): Array<Record<string, unknown>> {
  window.dataLayer = window.dataLayer ?? [];
  return window.dataLayer;
}

function toFirstPartyEvent(eventName: AnalyticsEventName, payload: AnalyticsPayload, eventId: string): AnalyticsEvent | undefined {
  if (typeof payload.quiz_session_id !== "string" || typeof payload.quiz_id !== "string" || typeof payload.quiz_slug !== "string" || typeof payload.quiz_version !== "number") return undefined;
  const event: AnalyticsEvent = {
    schema_version: 1,
    event_id: eventId,
    event_name: eventName,
    client_timestamp: new Date().toISOString(),
    quiz_session_id: payload.quiz_session_id,
    quiz_id: payload.quiz_id,
    quiz_slug: payload.quiz_slug,
    quiz_version: payload.quiz_version,
    page_path: window.location.pathname,
    device_type: deviceType(),
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight
  };
  const stringFields: Array<keyof AnalyticsEvent> = [
    "question_id", "answer_id", "next_question_id", "result_id", "result_mode", "result_state", "field_name", "error_code", "cta_id",
    "landing_path", "referrer_host", "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"
  ];
  const numberFields: Array<keyof AnalyticsEvent> = [
    "question_index", "progress_percent", "duration_ms", "result_score", "correct_count", "mistakes_count"
  ];
  for (const field of stringFields) {
    const value = payload[field];
    if (typeof value === "string") (event as unknown as Record<string, unknown>)[field] = value;
  }
  for (const field of numberFields) {
    const value = payload[field];
    if (typeof value === "number" && Number.isFinite(value)) (event as unknown as Record<string, unknown>)[field] = value;
  }
  if (typeof payload.is_correct === "boolean") event.is_correct = payload.is_correct;
  return event;
}

function deviceType(): AnalyticsDeviceType {
  if (window.innerWidth < 640) return "mobile";
  if (window.innerWidth < 1024) return "tablet";
  return "desktop";
}

function safeUuid(): string {
  return crypto.randomUUID ? crypto.randomUUID() : `evt-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
